import { useMemo, useState } from 'react'
import CLUB from '../../config/club.js'
import { fmtVsPar } from '../../lib/scoring.js'
import { SAMPLE_TOURNAMENT } from './sampleTournament.js'
import { STABLEFORD_MODIFIED_DEFAULT } from './ggFormats.js'
import { FORMAT_REGISTRY, DEFAULT_SELECTION, runFormats, combinedCsv } from './ggMultiFormat.js'

// G-MultiFormat — run several formats on ONE event at once, from the in-code
// SAMPLE_TOURNAMENT. Pure composition over the existing engines. READ-ONLY.
function download(name, text) {
  const blob = new Blob([text], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
}

export default function GMultiFormat() {
  const [selected, setSelected] = useState(() => new Set(DEFAULT_SELECTION))
  const [sfMode, setSfMode] = useState('standard')
  const [sfPoints, setSfPoints] = useState(STABLEFORD_MODIFIED_DEFAULT)
  const [carryover, setCarryover] = useState(true)
  const [validation, setValidation] = useState(false)
  const [pot, setPot] = useState(0)

  const options = {
    stableford: sfMode === 'modified' ? { mode: 'modified', points: sfPoints } : { mode: 'standard' },
    skins: { carryover, validation, potPerHole: Number(pot) || 0 },
  }
  const selKeys = FORMAT_REGISTRY.filter((r) => selected.has(r.key)).map((r) => r.key)
  const panels = useMemo(() => runFormats(SAMPLE_TOURNAMENT, selKeys, options),
    [selKeys.join(','), sfMode, sfPoints, carryover, validation, pot])

  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })
  const anyStableford = selected.has('stableford-gross') || selected.has('stableford-net')
  const anySkins = selected.has('skins-gross') || selected.has('skins-net')

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>

      {/* Games picker */}
      <div className="g-screen space-y-3 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-bold" style={{ color: 'var(--ge-primary)' }}>Games — {panels.length} running</span>
          <div className="flex gap-2">
            <button className="btn-ghost text-xs" onClick={() => setSelected(new Set(FORMAT_REGISTRY.map((r) => r.key)))}>✨ Turn on 10+</button>
            <button className="btn-ghost text-xs" onClick={() => setSelected(new Set())}>Clear all</button>
            <button className="btn-ghost text-xs" onClick={() => download('multiformat.csv', combinedCsv(panels))} disabled={panels.length === 0}>⬇ Combined CSV</button>
            <button className="btn-primary" onClick={() => window.print()} disabled={panels.length === 0}>🖨 Print</button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {FORMAT_REGISTRY.map((r) => (
            <label key={r.key} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={selected.has(r.key)} onChange={() => toggle(r.key)} />
              <span>{r.label}</span>
            </label>
          ))}
        </div>

        {(anyStableford || anySkins) && (
          <div className="flex flex-wrap gap-4 pt-2 text-xs" style={{ borderTop: '1px solid #eee' }}>
            {anyStableford && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-gray-600">Stableford</span>
                <select className="border rounded px-1 py-0.5" value={sfMode} onChange={(e) => setSfMode(e.target.value)}>
                  <option value="standard">Standard</option><option value="modified">Modified</option>
                </select>
                {sfMode === 'modified' && ['eagle', 'birdie', 'par', 'bogey', 'double'].map((k) => (
                  <label key={k} className="flex items-center gap-1"><span className="capitalize text-gray-500">{k}</span>
                    <input type="number" className="w-11 border rounded px-1" value={sfPoints[k]} onChange={(e) => setSfPoints((p) => ({ ...p, [k]: Number(e.target.value) }))} /></label>
                ))}
              </div>
            )}
            {anySkins && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-gray-600">Skins</span>
                <label className="flex items-center gap-1"><input type="checkbox" checked={carryover} onChange={(e) => setCarryover(e.target.checked)} />Carryover</label>
                <label className="flex items-center gap-1"><input type="checkbox" checked={validation} onChange={(e) => setValidation(e.target.checked)} />Validate</label>
                <label className="flex items-center gap-1"><span className="text-gray-500">Pot/hole $</span>
                  <input type="number" className="w-14 border rounded px-1" value={pot} onChange={(e) => setPot(e.target.value)} /></label>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Combined results — one panel per active game */}
      <div className="g-print">
        <div className="g-rpt-head">
          <img src={CLUB.logo} alt={CLUB.name} className="g-rpt-logo" />
          <div>
            <div className="g-rpt-club">{CLUB.name}</div>
            <div className="g-rpt-event">{SAMPLE_TOURNAMENT.info.name} · Multi-Format · {panels.length} games</div>
          </div>
        </div>
        {panels.length === 0 ? <p className="g-muted g-screen">No games selected.</p> : null}
        {panels.map((p) => (
          <div key={p.key} className="g-game">
            <h4 className="g-game-name">{p.label}</h4>
            <Panel panel={p} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Panel({ panel: p }) {
  if (p.kind === 'skins') {
    return (
      <table className="g-tbl"><thead><tr><th>Team</th><th>Skins</th><th>Value</th></tr></thead>
        <tbody>{p.result.teams.map((t) => <tr key={t.teamId}><td>{t.teamName}</td><td><b>{t.skins}</b></td><td>{t.value ? `$${t.value.toLocaleString()}` : '—'}</td></tr>)}</tbody></table>
    )
  }
  if (p.kind === 'payouts') {
    return (
      <table className="g-tbl"><thead><tr><th>Place</th><th>%</th><th>Amount</th><th>Team</th><th>Owner</th></tr></thead>
        <tbody>{p.result.rows.map((r) => <tr key={r.place}><td>{r.place}</td><td>{r.pct}%</td><td>${r.amount.toLocaleString()}</td><td>{r.teamName}</td><td>{r.owner}</td></tr>)}</tbody></table>
    )
  }
  if (p.kind === 'winners') {
    return (
      <table className="g-tbl"><thead><tr><th>Flight</th><th>1st</th><th>2nd</th><th>3rd</th></tr></thead>
        <tbody>{p.result.map((f) => <tr key={f.flightId}><td>{f.flightName}</td>{[0, 1, 2].map((i) => <td key={i}>{f.winners.gross[i]?.teamName || '—'}</td>)}</tr>)}</tbody></table>
    )
  }
  if (p.kind === 'matches') {
    return (
      <table className="g-tbl"><thead><tr><th>Flight</th><th>Match</th><th>Result</th><th>Winner</th></tr></thead>
        <tbody>{p.result.flatMap((f) => f.matches.map((m, i) => <tr key={`${f.flightId}-${i}`}><td>{f.flightName}</td><td>{m.teamAName} vs {m.teamBName}</td><td>{m.result}</td><td>{m.winnerName}</td></tr>))}</tbody></table>
    )
  }
  // standings: leaderboard / stableford / scramble
  const isStableford = p.key.startsWith('stableford')
  const isScramble = p.key.startsWith('scramble')
  return (
    <table className="g-tbl">
      <thead><tr><th>Pos</th><th>Team</th>{isStableford ? <th>Points</th> : isScramble ? <><th>Score</th><th>vs Par</th><th>Net</th></> : <><th>Gross</th><th>vs Par</th></>}</tr></thead>
      <tbody>{p.result.map((r) => (
        <tr key={r.teamId}><td>{r.place}</td><td>{r.teamName}</td>
          {isStableford ? <td><b>{r.points}</b></td>
            : isScramble ? <><td>{r.teamScore}</td><td>{fmtVsPar(r.vsPar)}</td><td>{r.netScore}</td></>
            : <><td>{r.gross}</td><td>{fmtVsPar(r.vsPar)}</td></>}
        </tr>
      ))}</tbody>
    </table>
  )
}

const PRINT_CSS = `
  .g-rpt-head { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid ${CLUB.accentColor}; margin-bottom: 14px; }
  .g-rpt-logo { height: 52px; object-fit: contain; }
  .g-rpt-club { font-weight: 800; font-size: 18px; color: ${CLUB.primaryColor}; }
  .g-rpt-event { font-size: 12px; color: #555; }
  .g-game { margin-bottom: 18px; page-break-inside: avoid; }
  .g-game-name { font-weight: 800; color: ${CLUB.primaryColor}; margin: 10px 0 6px; }
  .g-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-tbl th, .g-tbl td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  .g-tbl th { background: #f6f6f2; color: #555; }
  .g-muted { color: #999; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    @page { size: portrait; margin: 0.4in; }
  }
`
